import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';
import SeeItLive from './SeeItLive';
import AbbrTip from '@/components/AbbrTip';

const FAQS = [
  {
    q: 'What is Warren Buffett\u2019s $1 retained earnings test?',
    a: 'A rule Buffett laid out in his 1984 letter to Berkshire Hathaway shareholders: a company should keep (retain) its profits only if every dollar it keeps creates at least one dollar of market value for shareholders. If management cannot clear that bar, the money should be paid out as dividends or buybacks instead, because shareholders could earn more with it elsewhere. Buffett applies the test over rolling five-year periods so that a single year of market euphoria or panic does not decide the verdict.',
  },
  {
    q: 'How does DiviDrip calculate it?',
    a: 'Three steps from filed statements and daily prices. (1) Retained earnings generated: for every fiscal year in the window, net income minus cash dividends paid, summed. (2) Change in market cap: today\u2019s market cap minus the market cap at the base fiscal year-end (that day\u2019s split-adjusted close times the share count for that year). (3) Ratio: change in market cap divided by retained earnings. $1.00 or more passes; $2.00 or more is labelled a compounder; below $1.00 fails.',
  },
  {
    q: 'Why is the window 4 or 5 years instead of exactly 5?',
    a: 'Our price feed carries about five years of daily history, so the base year is the oldest fiscal year-end still inside that limit \u2014 four years for most companies right now, five for some, never fewer than three. Because the result is a per-dollar ratio rather than a total, a 4-year and a 5-year window are directly comparable. Every row and card shows the exact window it used.',
  },
  {
    q: 'Why are banks, REITs, MLPs and biotech not tested?',
    a: 'Each one breaks a different part of the math. Banks and insurers are leveraged by design, so Debt-to-Equity is enormous and ROE is inflated without that meaning anything bad. REITs must distribute roughly 90% of taxable income, so they have almost no retained earnings to test. MLPs are pass-through entities with the same problem. Biotech and other pre-revenue companies have negative earnings, which gives negative P/E and negative ROE and nothing retained. Funds, ETFs, preferreds and SPAC shells are not operating companies at all. DiviDrip excludes these by category before pulling any numbers, then applies three statement-level floors: revenue above $10 million, positive latest net income, and positive shareholders\u2019 equity.',
  },
  {
    q: 'What does the "Peak-cycle spike" caution mean?',
    a: 'It fires when net income jumped three times or more in the latest fiscal year, swung from a loss to a profit, or revenue grew 60% or more. Those are the fingerprints of a cyclical company at the top of its cycle. The ratio, ROE and P/E are all computed correctly \u2014 they are just measured at the best moment the business has ever had, and they can reverse. The caution never changes the math; it tells you to look at the full cycle before trusting the numbers.',
  },
  {
    q: 'Why does Micron pass with a huge ratio but still carry two caution flags?',
    a: 'Micron kept roughly $86 billion over fiscal 2023\u20132026 and its market cap rose by far more than that, so the $1 test result is about $10\u201312 of market value per dollar retained \u2014 a clear pass. But fiscal 2023 was a $5.8 billion loss year and fiscal 2026 net income was about ten times fiscal 2025, so the card shows "Peak-cycle spike" and "Loss year in window". Both are true at the same time: management did compound retained capital superbly, and the latest-year ROE and P/E are peak-of-cycle readings that will not hold if memory prices roll over.',
  },
  {
    q: 'What is the combo score?',
    a: 'One point for each of four buckets: Return on Equity of 15% or higher, P/E of 25 or lower, Debt-to-Equity below 1.0, and a passing $1 test. A 4/4 is what the page calls the "perfect combo" \u2014 highly profitable, reasonably priced, financially safe, and proven to turn kept profits into value. Most companies land at 2 or 3; a 4/4 with no caution icons is rare and worth a close read of the filings.',
  },
  {
    q: 'What does "Not meaningful \u2014 almost nothing retained" mean?',
    a: 'If retained earnings over the window were zero, negative, or under 2% of the starting market cap, the division produces a wild number that says nothing about management. A company that paid out essentially everything, or that earned nothing on net across the window, is listed separately with no pass or fail assigned.',
  },
  {
    q: 'Does the test replace the dividend-safety tools on DiviDrip?',
    a: 'No. It answers a different question \u2014 is management a good allocator of the money it keeps? \u2014 and it says nothing about whether next quarter\u2019s dividend is safe. Pair it with the Triangle Scorecard, the Accounting Anomalies checklist and the Financial Health tab. A great $1 test with four accounting anomalies is a question, not a buy.',
  },
];

export default function LearnBuffettOneDollarTest() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title={"Buffett\u2019s $1 Retained Earnings Test \u2014 Did Every Kept Dollar Earn Its Keep? | TwylightCrow"}
      description={"Warren Buffett\u2019s 1984 rule that every dollar of retained earnings must create at least a dollar of market value. The exact math DiviDrip runs over 4\u20135 fiscal years, the ROE / P/E / Debt-to-Equity benchmark cheat sheet, what is excluded and why, and the Micron peak-cycle lesson."}
      canonical="https://twylightcrow.com/learn/buffett-one-dollar-test"
      guidePath="/learn/buffett-one-dollar-test"
      sectionLabel="Value Frameworks"
      sectionAnchor="value-frameworks"
      kicker={"Learn \u00b7 Value Frameworks"}
      h1={"Buffett\u2019s $1 Retained Earnings Test \u2014 Did Every Kept Dollar Earn Its Keep?"}
      datePublished="2026-11-04"
      jsonLd={jsonLd}
      related={[
        { to: '/buffett-test', label: 'Buffett $1 Retained Earnings \u2014 the live page, every stock that passes, ranked' },
        { to: '/learn/accounting-anomalies-enron-checklist', label: 'Reading a Company Like a Short-Seller \u2014 the Enron Checklist' },
        { to: '/learn/graham-piotroski-deep-value', label: 'Graham + Piotroski \u2014 Deep Value Without the Traps' },
        { to: '/learn/cyclical-growth-investing', label: 'Cyclical Growth Investing \u2014 when peak earnings lie' },
        { to: '/learn/dividend-traps', label: 'Dividend Traps \u2014 and when the trap detector is wrong' },
      ]}
    >
      <p>
        Most stock screens ask what a company earns. Buffett&rsquo;s test asks a
        sharper question: <em>what did management do with the part it kept?</em>{' '}
        A company that earns $10 billion and pays out $4 billion has retained
        $6 billion of its owners&rsquo; money. Either that $6 billion shows up
        later as more than $6 billion of market value &mdash; or the owners
        would have been better off receiving it as a dividend and investing it
        themselves.
      </p>
      <p>
        That is the whole test. Buffett wrote it down in his{' '}
        <a href="https://www.berkshirehathaway.com/letters/1984.html" target="_blank" rel="noopener noreferrer">1984 letter to shareholders</a>:
        unrestricted earnings should be retained only when there is a reasonable
        prospect that <strong>for every dollar retained, at least one dollar of
        market value will be created</strong>. He added two guard-rails that
        matter just as much. Judge it over a <strong>five-year rolling
        period</strong>, not a single year, so market moods wash out. And watch
        for what he called the <em>institutional imperative</em> &mdash;
        managers who keep earnings to build an empire rather than because they
        can invest them well.
      </p>

      <h2>The math DiviDrip runs</h2>
      <table className="learn-table">
        <thead>
          <tr>
            <th>Step</th>
            <th>Formula</th>
            <th>Where the numbers come from</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">1. Retained earnings generated</td>
            <td className="font-mono">&Sigma; (Net income &minus; Dividends paid) over the window</td>
            <td>Net income from each fiscal year&rsquo;s income statement; cash dividends from the cash-flow statement. A loss year subtracts.</td>
          </tr>
          <tr>
            <td className="font-semibold">2. Change in market cap</td>
            <td className="font-mono">Market cap today &minus; Market cap at base year-end</td>
            <td>Base year-end: split-adjusted close that day &times; that year&rsquo;s basic share count. Today: live market cap.</td>
          </tr>
          <tr>
            <td className="font-semibold">3. The ratio</td>
            <td className="font-mono">Change in market cap &divide; Retained earnings</td>
            <td>&ge; $1.00 passes &middot; &ge; $2.00 compounder &middot; &lt; $1.00 fails</td>
          </tr>
        </tbody>
      </table>
      <p>
        One subtlety that trips up home-made spreadsheets: do not read the
        &ldquo;retained earnings&rdquo; line off the balance sheet and subtract
        two years. That line is polluted by buybacks, accounting changes and
        impairments. Build it yourself from net income minus dividends, year by
        year, so you know exactly what went in.
      </p>
      <p>
        A second subtlety is share counts. Data vendors restate historical share
        counts after a split, so the base-year price has to be split-adjusted too
        or a 20-for-1 split turns a compounder into a catastrophe on paper.
        DiviDrip pairs the adjusted close with the restated count so the two
        always speak the same language.
      </p>

      <h2>Why the window is 4&ndash;5 years, not exactly 5</h2>
      <p>
        Our price feed carries roughly five years of daily bars. The base year
        is therefore the oldest fiscal year-end that still sits inside that
        limit &mdash; four fiscal years back for most companies today, five for
        some, and never fewer than three. Because the output is a per-dollar
        ratio rather than a total, a 4-year and a 5-year result are directly
        comparable, and every row on the page prints the window it used.
      </p>

      <h2>The three companions: ROE, P/E and Debt-to-Equity</h2>
      <p>
        The $1 test tells you whether kept money compounded. It does not tell
        you whether the business is efficient, cheap, or safe. Three classic
        ratios fill those gaps, and the benchmarks below are the general
        large-cap baselines &mdash; a utility and a software company live on
        different curves, so always compare with sector peers and the
        company&rsquo;s own history.
      </p>
      <table className="learn-table">
        <thead>
          <tr>
            <th>Metric</th>
            <th>Bad</th>
            <th>Neutral</th>
            <th>Good / Best</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold"><AbbrTip term="ROE">Return on Equity</AbbrTip></td>
            <td>Below 10%</td>
            <td>10%&ndash;15%</td>
            <td>15%&ndash;20%+ &mdash; but 50%+ with debt above equity is a leverage check, not a trophy</td>
          </tr>
          <tr>
            <td className="font-semibold"><AbbrTip term="TRAILING_PE">Price-to-Earnings</AbbrTip></td>
            <td>Above 30, or negative</td>
            <td>15&ndash;25</td>
            <td>Under 15</td>
          </tr>
          <tr>
            <td className="font-semibold"><AbbrTip term="DEBT_EQUITY">Debt-to-Equity</AbbrTip></td>
            <td>Above 2.0</td>
            <td>1.0&ndash;2.0</td>
            <td>Under 1.0</td>
          </tr>
          <tr>
            <td className="font-semibold">$1 test</td>
            <td>Under $1.00</td>
            <td>$1.00&ndash;$2.00</td>
            <td>$2.00 and up</td>
          </tr>
        </tbody>
      </table>
      <p>
        DiviDrip folds these into a <strong>combo score</strong> out of four:
        one point each for ROE of 15% or more, P/E of 25 or less, D/E under 1.0,
        and a passing $1 test. A 4/4 is the &ldquo;perfect combo&rdquo; &mdash;
        highly profitable, fairly priced, financially safe, and proven to turn
        kept profits into value. The opposite pattern is the one to fear: a
        sky-high ROE paired with a failing $1 test means the company is
        profitable on paper but the market is not buying it, which usually
        points to bad acquisitions or hidden risk.
      </p>

      <h2>What is deliberately left out</h2>
      <ul>
        <li><strong>Banks, insurers, BDCs &mdash; the whole Financials sector.</strong> Leverage is the business model. Their Debt-to-Equity would fail every screen and their ROE is inflated by it, so neither number is comparable.</li>
        <li><strong>REITs and MLPs.</strong> Required to pay out almost everything, so there are no retained earnings to test. Judge them on FFO and distributable cash instead.</li>
        <li><strong>Biotech and pre-revenue names.</strong> Negative earnings mean negative P/E, negative ROE, and nothing retained.</li>
        <li><strong>Commodity cyclicals</strong> (Energy, Basic Materials) stay <em>in</em> the test but carry a caution icon, because a spectacular ratio at the top of a commodity boom is not a moat.</li>
        <li><strong>SPACs, funds, ETFs, CEFs, preferreds, warrants, ADRs.</strong> Not operating companies, or statements in a home currency that will not pair with a US-listed price.</li>
        <li><strong>Statement floors.</strong> Revenue above $10 million, positive latest net income, positive shareholders&rsquo; equity, at least three usable fiscal years.</li>
      </ul>

      <h2>The Micron lesson &mdash; when a $12 result still needs a flag</h2>
      <p>
        Micron Technology is the cleanest illustration of why the page attaches
        cautions instead of silently trusting the arithmetic. Over fiscal
        2023&ndash;2026 Micron kept roughly $86 billion. Its market cap rose by
        many multiples of that, so the $1 test comes back at about $10&ndash;$12
        of market value per dollar retained &mdash; an emphatic pass, and the
        combo score is 4/4.
      </p>
      <p>
        Look one layer down and the window contains a $5.8 billion loss year
        (fiscal 2023, when memory prices collapsed) followed by a fiscal 2026 in
        which net income was roughly ten times the year before. That is the
        signature of a cyclical business at the very top of its cycle. The ROE
        is real, the P/E is real, and both are measured at the single best
        moment the company has ever had. If memory prices roll over, those two
        numbers will not hold even though the past compounding genuinely
        happened.
      </p>
      <p>
        So the card shows both truths at once: the pass, and two amber icons
        &mdash; <strong>Peak-cycle spike</strong> and <strong>Loss year in
        window</strong>. The same logic explains why forensic scores like the
        Beneish M-Score and Montier C-Score light up on Micron: receivables,
        inventory and assets all ballooned because revenue grew 256% in one
        year, not because anyone cooked the books. We never bend the math to
        make a stock look better or worse; we label the context and let you
        read the full cycle. <SeeItLive ticker="MU" tab="financial-health" tone="amber" label={"See MU\u2019s $1 test card live \u2192"} />
      </p>

      <h2>Reading the page</h2>
      <p>
        The <Link to="/buffett-test">Buffett $1 Retained Earnings page</Link> runs the test
        on every operating company with filed statements and refreshes weekly.
        It opens on dividend payers; the All and Non-Dividend views use the same
        3-way switch as the Screener. The <em>Perfect combo</em> table is the
        shortlist; the <em>Passed</em> table is ranked by combo score and then
        by dollars created; the <em>Failures</em> list shows companies whose kept
        profits shrank in the market; and <em>Not meaningful</em> gathers
        companies that retained almost nothing, where dividing by a near-zero
        number would produce noise rather than insight. Inside any stock&rsquo;s
        modal, the Financial Health tab carries the same result with the
        year-by-year retention table.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }, i) => (
          <div key={i} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <p className="text-xs text-muted-foreground italic">
        This guide is educational. The $1 test measures past capital allocation
        against a moving market price; it is not a forecast and not a
        recommendation. Pair it with the Triangle Scorecard, the Accounting
        Anomalies checklist and the latest 10-K before committing capital.
      </p>
    </LearnPage>
  );
}
